import React, { useCallback, useEffect, useState } from 'react';

import { TooltipStateful } from '@yandex-360/orb-components/dist/components/Tooltip';
import { useSelector } from 'react-redux';

import Button from '../../../../shared/components/button';
import { ModalStopRecordingConfirm } from '../../../../shared/components/modal-stop-recording-confirm';
import ModerationPopup from '../../../../shared/components/moderation-popup';
import SvgImage from '../../../../shared/components/svg-image';
import { SELF } from '../../../../shared/constants';
import {
    isAddMembersButtonDisabled as isAddMembersButtonDisabledSelector,
    isMessengerIntegration as isMessengerIntegrationSelector,
    isMessengerIntegrationTier,
} from '../../../../shared/features/config/selectors';
import useShouldShowModerationControls from '../../../../shared/features/moderation/hooks/useShouldShowModerationControls';
import { i18n } from '../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { ParticipantID } from '../../types';
import { InviteOrCopyFooter } from '../invite-or-copy-footer';
import styles from './styles.css';

const moreIcon = <SvgImage imageName="more" />;
export const ParticipantsFooter = () => {
    const isAddMembersButtonDisabled = useSelector(isAddMembersButtonDisabledSelector);

    const isMessengerIntegration = useSelector(isMessengerIntegrationSelector);
    const isMessengerIntegrationTier1 = useSelector((state) => isMessengerIntegrationTier(state, 1));
    const isMessengerIntegrationTier0 = isMessengerIntegration && !isMessengerIntegrationTier1;

    const { shouldShowModerationPopupButton } = useShouldShowModerationControls(SELF);
    const moreButtonRef = React.useRef<HTMLDivElement>(null);

    const [showModerationPopup, setShowModerationPopup] = useState<boolean>(false);
    const handleCloseModerationPopup = useCallback(() => setShowModerationPopup(false), []);
    const handleToggleModerationPopup = useCallback(() => setShowModerationPopup((value) => !value), []);

    const [participantIdToStopRecording, setParticipantIdToStopRecording] = useState<ParticipantID | 'all' | null>(
        null
    );
    const handleShowModalStopRecording = useCallback((participantId: ParticipantID | 'all') => {
        setParticipantIdToStopRecording(participantId);
    }, []);
    const handleCloseModalStopRecordingConfirm = useCallback(() => setParticipantIdToStopRecording(null), []);

    useEffect(() => {
        if (showModerationPopup) {
            countMetrika('meeting_screen', 'participants_list', 'more_button');
        }
    }, [showModerationPopup]);

    return !isMessengerIntegrationTier0 && !isAddMembersButtonDisabled ? (
        <>
            <InviteOrCopyFooter
                className={styles.inviteOrCopyFooter}
                addonRight={
                    shouldShowModerationPopupButton && (
                        <>
                            {showModerationPopup ? (
                                <ModerationPopup
                                    participantId={SELF}
                                    placement={'top right'}
                                    offset={8}
                                    triggerRef={moreButtonRef}
                                    onClose={handleCloseModerationPopup}
                                    onStopRecordingConfirm={handleShowModalStopRecording}
                                />
                            ) : null}
                            <TooltipStateful
                                preserveTabIndex={true}
                                content={i18n('toolbar', 'more-popup-alt-title')}
                                trigger="hover"
                            >
                                <div ref={moreButtonRef} className={styles.moderationPopupButton} tabIndex={-1}>
                                    <Button
                                        view="neutral"
                                        size="md"
                                        icon={moreIcon}
                                        onPress={handleToggleModerationPopup}
                                        testId="more-popup-alt-button"
                                    />
                                </div>
                            </TooltipStateful>
                        </>
                    )
                }
            />
            {participantIdToStopRecording ? (
                <ModalStopRecordingConfirm
                    participantId={participantIdToStopRecording}
                    onClose={handleCloseModalStopRecordingConfirm}
                />
            ) : null}
        </>
    ) : null;
};
